import { useEffect, useState } from "react";
import markdownit from "markdown-it";
import { Copy, Printer, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { copyTakeawayToClipboard, openTakeawayPrintWindow } from "@/services/teacherScenarioExportService";
import { toast } from "sonner";

const md = markdownit({ html: true, linkify: true, typographer: true });

interface ScenarioTakeawayPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  markdown: string;
  scenarioTitle: string;
  defaultAuthorName?: string;
}

export const ScenarioTakeawayPreview = ({
  open,
  onOpenChange,
  markdown,
  scenarioTitle,
  defaultAuthorName = "",
}: ScenarioTakeawayPreviewProps) => {
  const html = md.render(markdown);
  const [authorName, setAuthorName] = useState(defaultAuthorName);

  useEffect(() => {
    if (open) setAuthorName(defaultAuthorName);
  }, [open, defaultAuthorName]);

  const handlePrint = () => {
    const ok = openTakeawayPrintWindow(markdown, scenarioTitle, authorName);
    if (!ok) toast.error("Allow pop-ups to print or save as PDF.");
  };

  const handleCopy = async () => {
    const ok = await copyTakeawayToClipboard(markdown, authorName);
    toast[ok ? "success" : "error"](ok ? "Copied to clipboard." : "Could not copy — try print instead.");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle className="font-literata text-bodhi-teal">Principal takeaway preview</DialogTitle>
          <DialogDescription>
            Add your name, then print or copy. The PDF includes the HeyBodhi logo and notes it was created in the
            Teacher Scenario Practice tool.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="takeaway-author">Your name (optional — shows on the document)</Label>
          <Input
            id="takeaway-author"
            value={authorName}
            maxLength={80}
            placeholder="e.g. Jordan Rivera"
            onChange={(e) => setAuthorName(e.target.value)}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="chat-message-content prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            <X className="mr-1.5 h-4 w-4" />
            Close
          </Button>
          <Button type="button" variant="outline" onClick={() => void handleCopy()}>
            <Copy className="mr-1.5 h-4 w-4" />
            Copy
          </Button>
          <Button type="button" className="bg-bodhi-teal hover:bg-bodhi-teal/90" onClick={handlePrint}>
            <Printer className="mr-1.5 h-4 w-4" />
            Print / Save as PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
